import { Trans, t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { X, User } from "lucide-react";
import { FC, ReactNode, useEffect, useState } from "react";
import { useAppUIContext } from "../../../context/AppUIContext";
import { Room } from "../../../models/Room";
import { RoomItem } from "../../../models/RoomItem";
import { FilterType } from "../../../models/local/Types";
import { Fire } from "../../../services/FireClient";
import { My } from "../../../services/My";
import { Util } from "../../../services/Utils/Util";
import ItemPopover from "../../components/ItemPopover";
import RoomSettingsModal from "../../components/RoomSettingsModal";
import { PanelListBots } from "./PanelListBots";
import { PanelListImages } from "./PanelListImages";
import { PanelListMembers } from "./PanelListMembers";
import { lazy, Suspense } from "react";
import { IS_QA_ELIGIBLE } from "../../../config/qaFeedback";

// 통역 링크 (dev 전용) — lazy: 통역 스택(@google/genai)이 메인 번들에 실리지 않게
const ItpCallLinkButton = lazy(() => import("../../components/ItpCallLinkButton"));
import { PanelListTokens } from "./PanelListTokens";
import { PanelMemos } from "./PanelMemos";
import { PanelPagecall } from "./PanelPagecall";
import { PanelChannelBoards } from "./PanelChannelBoards";
import { EnterType } from "./RoomFilesModal";
import { Button } from "../../../components/ui/button";
import { Card } from "../../../components/ui/card";
import { SectionCard, SectionActionButton, SectionListItem } from "./SectionCard";
import { Loader2 } from "lucide-react";
import { useResponsive, hideRightMenu } from "../../../hooks/useResponsive";

export interface IRoomRightMenu {
  type: FilterType,
  room: Room,
  onClose?: () => void;
}
type ItemListTypes = 'officenote' | 'pagecall' | 'files' | 'images' | 'google-drive' | 'links' | 'memos';

export const RoomRightMenu: FC<IRoomRightMenu> = ({ room, type, onClose }) => {
  useLingui();
  const { setEnterType, setShowFilesModal } = useAppUIContext();
  const [items] = useState<Map<ItemListTypes, RoomItem[]>>(new Map());
  const [inProgressItems, setInProgressItems] = useState(false);
  const isPersonalRoom = room.visibility === 'personal';
  const { isMobileOrTablet } = useResponsive();

  useEffect(() => {
    hideRightMenu();
    setInProgressItems(true)
    items.clear();
    return Fire.item.subscribeToRecentRoomItems(room.id, (list) => {
      setInProgressItems(true);
      items.clear();
      items.set('officenote', []);
      items.set('pagecall', []);
      items.set('memos', []);
      items.set('files', []);
      items.set('images', []);
      items.set('google-drive', []);
      items.set('links', []);
      list.sort((a, b) => a.pinned && !b.pinned ? -1 : 1).forEach((item) => {
        if (item.type === 'officenote') {
          items.get('officenote')?.push(item);
        } else if (item.type === 'pagecall') {
          items.get('pagecall')?.push(item);
        } else if (item.type === 'memo') {
          items.get('memos')?.push(item);
        } else if (item.type === 'file') {
          if (Util.isImageUrl(item.url))
            items.get('images')?.push(item);
          else
            items.get('files')?.push(item);
        } else if (item.type === 'link') {
          if (item.url?.includes('docs.google.') || item.url?.includes('drive.google.'))
            items.get('google-drive')?.push(item);
          else
            items.get('links')?.push(item);
        }
      });

      setTimeout(() => {
        setInProgressItems(false);
      }, 100);
    });
  }, [room.id])

  function showFilesModal(fileFilter: EnterType) {
    setShowFilesModal(true);
    setEnterType(fileFilter);
  }

  function InvitedEmailList() {
    if (room?.invitedUserEmails && room?.invitedUserEmails?.length > 0) {
      return (
        <SectionCard title={t`Joins on sign-up`} collapsible={false}>
          <div className="flex flex-col">
            {room.invitedUserEmails.map((email) => (
              <SectionListItem key={email}>
                <User className="w-5 h-5 text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-[color:rgb(var(--text-body))] truncate">{email}</p>
                  <p className="text-xs text-muted-foreground"><Trans>Unregistered</Trans></p>
                </div>
              </SectionListItem>
            ))}
          </div>
        </SectionCard>
      );
    }
    return null;
  }

  function personalPanel() {
    return (
      <div className="sticky top-0 z-10 bg-background px-4 py-4 border-b border-border flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          <Trans>This channel is only for you.</Trans>
        </p>
        {isMobileOrTablet && closeButton}
      </div>
    );
  }

  const closeButton = (
    <Button
      variant="icon-panel"
      onClick={() => onClose?.()}
      data-cy="close-right-menu"
    >
      <X />
    </Button>
  );

  function RoomSettings({ room }: { room: Room }) {
    return (
      <>
        <RoomSettingsModal room={room} headerAction={isMobileOrTablet ? closeButton : undefined} />
      </>
    );
  }

  return (
    <div className="w-[304px] flex flex-col relative z-10 flex-shrink-0 h-full">
      <div className="flex-1 overflow-y-auto ios-top-margin ios-bottom-padding bg-background md:rounded-panel md:border md:border-border overflow-hidden">
        {isMobileOrTablet && (isPersonalRoom || room.isOneToOne) && !isPersonalRoom && (
          <div className="sticky top-0 z-10 bg-background p-2 border-b border-border flex justify-end">
            {closeButton}
          </div>
        )}

        {/* 컨텐츠 영역 - Figma: 연속 패널 구조 */}
        <div className="flex flex-col bg-background">
          {!isPersonalRoom && (
            <>
              {!room.isOneToOne && RoomSettings({ room: room })}
              <PanelListMembers room={room} />
            </>
          )}

          {isPersonalRoom && (
            <>
              {personalPanel()}
              {/* 통역 통화 링크 — 개인 기능으로 시작 (검증 후 1:1 → 일반 채널 확장 예정) */}
              {IS_QA_ELIGIBLE && (
                // roomId = 지금 보고 있는 개인방 — 워크스페이스마다 개인방이 다르므로
                // (기본은 {uid}, 그 외는 {workspaceId}_{uid}) 링크·회의록도 이 방에 귀속된다
                <Suspense fallback={null}><ItpCallLinkButton roomId={room.id} /></Suspense>
              )}
            </>
          )}

          {/* 채널 게시판 바로가기 — 채널에 연결된 게시판이 있을 때만 표시 */}
          {!isPersonalRoom && !room.isOneToOne && (
            <PanelChannelBoards roomId={room.id} />
          )}

          <PanelMemos room={room} items={items.get('memos') ?? []} />

          <PanelListImages
            room={room}
            items={items.get('images') ?? []}
            buttons={
              <SectionActionButton
                onClick={() => showFilesModal('images')}
                label={t`View all`}
              />
            }
          />

          <ItemList
            className="documents"
            title={t`Documents`}
            room={room}
            items={items.get('files') ?? []}
            buttons={
              <SectionActionButton
                onClick={() => showFilesModal('documents')}
                label={t`View all`}
              />
            }
          />

          {items.size > 0 && (
            <>
              {(room.workspaceId == "odo_bang" || room.workspaceId == "jiranfamily") && (
                <ItemList
                  className="office-note"
                  title={t`OfficeNote`}
                  room={room}
                  items={items.get('officenote') ?? []}
                  buttons={null}
                />
              )}
              <ItemList
                className="google-drive"
                title={t`Google Drive`}
                room={room}
                items={items.get('google-drive') ?? []}
                buttons={null}
              />
              <ItemList
                className="links"
                title={t`Links`}
                room={room}
                items={items.get('links') ?? []}
                buttons={null}
              />
              {/* 마지막 섹션 하단 divider — Figma: 6px */}
              <div className="h-1.5 bg-muted w-full" />
            </>
          )}

          <InvitedEmailList />

          {/* API Tokens — 제일 하단에 배치 */}
          {!isPersonalRoom && !room.isOneToOne && <PanelListTokens room={room} />}

          {!isPersonalRoom && (room.workspaceId == "odo_bang" || room.workspaceId == "jiranfamily") && (
            <>
              <PanelListBots room={room} />
              <PanelPagecall room={room} items={items.get('pagecall') ?? []} />
            </>
          )}

          {inProgressItems && (
            <div className="flex justify-center py-4">
              <Loader2 className="w-6 h-6 text-muted-foreground animate-spin" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export const ContentCard: React.FC<{ content: () => React.ReactNode }> = ({ content }) => {
  return (
    <Card className="p-4">
      {content()}
    </Card>
  );
}

export const RightIframe: React.FC<{ item: RoomItem }> = ({ item }) => {
  const { setIframeItem } = useAppUIContext();

  return (
    <div className="w-[50vw] h-full flex flex-col border-l border-border z-50 bg-background relative flex-shrink-0">
      <div className="flex items-center gap-2 p-2 border-b">
        <Button variant="ghost" size="icon" onClick={() => setIframeItem(null)}>
          <X className="w-5 h-5" />
        </Button>
        <span className="text-sm truncate">
          {item.title && <span className="font-medium">{item.title} | </span>}
          {item.url}
        </span>
      </div>
      <div className="flex-1">
        <iframe src={item.url} title={item.url} className="w-full h-full border-0" />
      </div>
    </div>
  );
};

// ===========================================================================================================

const ItemList: FC<{ className: string, title: string, room: Room, items: RoomItem[], buttons: ReactNode | null }> = ({ className, title, room, items, buttons }) => {
  useLingui();
  const [isCompact, setIsCompact] = useState(true);
  const MAX_COMPACT = 3;
  const [filterKey, setFilterKey] = useState<string | null>(null);
  const [filteredItems, setFilteredItems] = useState<RoomItem[]>([]);

  useEffect(() => {
    setIsCompact(true);
    setFilteredItems(items.slice(0, MAX_COMPACT));
    setFilterKey(null);
  }, [room.id, items])

  function toggleCompact() {
    const _isCompact = !isCompact;
    setIsCompact(_isCompact);
    setFilteredItems(_isCompact ? items.slice(0, MAX_COMPACT) : items);
  }

  function filterList(key: string | null) {
    setFilterKey(key);
    if (key) {
      const k = key.trim().toLowerCase();
      setFilteredItems(items.filter(i => {
        if (i.type == 'file') {
          return i.title?.toLowerCase().includes(k);
        } else {
          return i.url?.toLowerCase().includes(k) || i.title?.toLowerCase().includes(k);
        }
      }));
    } else {
      setFilteredItems(items);
    }
  }

  if (!items.length && className !== 'documents')
    return null;

  return (
    <SectionCard
      title={title}
      count={items.length}
      collapsible={true}
      defaultExpanded={true}
      actions={buttons}
    >
      {/* 필터 입력 (펼쳐진 상태에서만) */}
      {!isCompact && (
        <div className="mb-2 flex items-center gap-2">
          <input
            type="text"
            placeholder={t`Filter list`}
            value={filterKey || ''}
            onChange={(e) => filterList(e.target.value || null)}
            className="flex-1 px-3 py-1.5 text-sm border border-border rounded-md bg-background text-[color:rgb(var(--text-body))] focus:outline-none focus:ring-1 focus:ring-primary/50"
          />
          {filterKey && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => filterList(null)}
              className="h-8 w-8"
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      )}

      {/* 아이템 리스트 */}
      {filteredItems.length > 0 ? (
        <div className="flex flex-col gap-1">
          {filteredItems.map((i) => (
            <div key={i.id}>
              <ItemPopover
                item={i}
                canEdit={[room.creatorId, i.user.id].includes(My.user.id)}
                compact={isCompact}
              />
            </div>
          ))}
        </div>
      ) : (
        <p className="text-body font-medium text-muted-foreground py-3 text-center">
          <Trans>No items</Trans>
        </p>
      )}

      {/* 더보기 버튼 */}
      {isCompact && items.length > MAX_COMPACT && (
        <button
          onClick={toggleCompact}
          className="w-full mt-2 text-sm text-muted-foreground hover:text-foreground transition-colors py-1 cursor-pointer"
        >
          <Trans>{items.length - MAX_COMPACT} more items</Trans>
        </button>
      )}
    </SectionCard>
  );
}